import React from 'react';
import { useRadarChart } from '../context/shared/RadarChartContext';
import MentalHealthRadarChart from '../components/common/MentalHealthRadarChart';
import PageContainer from '../components/common/PageContainer';
import PageSection from '../components/common/PageSection';
import HelpTooltip from '../components/common/HelpTooltip';
import { useTheme } from '../context/shared/ThemeContext';
import ConsentForm from '../components/common/ConsentForm';

const Radar: React.FC = () => {
    const { isPatient, requiresConsent, grantConsent, loading } = useRadarChart();
    const { themeClasses } = useTheme();

    const renderContent = () => {
        if (loading) {
            return (
                <div className="flex justify-center items-center h-[400px]">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
                </div>
            );
        }

        // Patients should see their own radar chart, not the "managed by therapist" message

        if (requiresConsent) {
            return (
                <ConsentForm
                    onConsent={grantConsent || (() => Promise.resolve())}
                    featureName="Symbolic Radar Insights"
                    featureDescription="This feature analyzes patterns in your conversations to generate a symbolic visualization of your mental and emotional state. Your raw conversation data is not stored, only the resulting anonymized metrics. This helps you track your progress over time."
                />
            );
        }

        return <MentalHealthRadarChart />;
    };

    return (
        <PageContainer>
            <PageSection title="Symbolic Radar" emoji="📡">
                <div className={`p-6 rounded-lg ${themeClasses.cardBg} ${themeClasses.border}`}>
                    <div className="flex items-center justify-between mb-4">
                        <h3 className={`text-xl font-semibold ${themeClasses.primaryText}`}>Your Weekly Snapshot</h3>
                        <HelpTooltip 
                            title="Symbolic Radar Chart"
                            content="This chart visualizes your mental and emotional patterns based on your conversations. Each axis represents a different wellness dimension, and the scores show how present each pattern is in your recent activity. Higher scores indicate stronger presence of that pattern."
                            position="left"
                            size="lg"
                        />
                    </div>
                    <p className={`mt-2 text-sm ${themeClasses.secondaryText} mb-6`}>
                        This chart visualizes symbolic patterns based on recent activity, offering a snapshot of your mental and emotional landscape. It is not for medical use.
                    </p>
                    <div className="mt-6">
                        {renderContent()}
                    </div>
                    
                    {/* Additional help section */}
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                        <div className="flex items-start space-x-3">
                            <div className="text-blue-600 text-lg">💡</div>
                            <div className="flex-1">
                                <h4 className={`font-semibold text-blue-900 mb-2`}>
                                    Understanding Your Radar Chart
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
                                    <div>
                                        <p className="font-medium mb-1">📊 Scores (0-100):</p>
                                        <p>Higher scores = stronger presence of that pattern in your conversations</p>
                                    </div>
                                    <div>
                                        <p className="font-medium mb-1">📈 Trends:</p>
                                        <p>Arrows show changes from last week (↑ improving, ↓ declining, → stable)</p>
                                    </div>
                                    <div>
                                        <p className="font-medium mb-1">🎯 Dimensions:</p>
                                        <p>Each axis represents a different aspect of your mental wellness</p>
                                    </div>
                                    <div>
                                        <p className="font-medium mb-1">⏰ Updates:</p>
                                        <p>Chart updates weekly based on your recent conversation patterns</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </PageSection>
        </PageContainer>
    );
};

export default Radar; 